import { Alert, Button, Collapse, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { DocumentWithDetails } from '../../api/registryImportApi';
import RegistryQuestionnaireView, {
  inferQuestionnaireProgram,
  type RegistryQuestionnaireProgram,
} from '../../components/registry/RegistryQuestionnaireView';
import {
  CONSENT_OPTIONS,
  DOC_TYPE_LABELS,
  ENGINE_LABELS,
  FIELDS_BY_DOC_TYPE,
  FIELD_LABELS,
  LONG_TEXT_FIELDS,
  RECLASSIFY_OPTIONS,
} from './labels';
import { looksLikeOcrNoise } from './ocrNoise';

const { Text } = Typography;

/** Câmpurile listă editate ca text: separator rând nou sau virgulă. */
const NEWLINE_LISTS = new Set(['diagnosisLines', 'secondaryDiagnosisTexts']);
const COMMA_LISTS = new Set(['secondaryIcd10']);
const NUMBER_FIELDS = new Set(['breslowMm', 'clarkLevel', 'age']);
const BOOLEAN_FIELDS = new Set(['ulceration', 'hpMissing']);

/** Valoarea stocată → text editabil. */
export function toEditable(field: string, value: unknown): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.join(NEWLINE_LISTS.has(field) ? '\n' : ', ');
  if (typeof value === 'boolean') return value ? 'da' : 'nu';
  return String(value);
}

/** Text editat → valoarea stocată (gol = câmp absent). */
export function fromEditable(field: string, text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  if (NEWLINE_LISTS.has(field)) return trimmed.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (COMMA_LISTS.has(field)) return trimmed.split(',').map((l) => l.trim()).filter(Boolean);
  if (NUMBER_FIELDS.has(field)) {
    const n = Number(trimmed.replace(',', '.'));
    return Number.isFinite(n) ? n : trimmed;
  }
  if (BOOLEAN_FIELDS.has(field)) return /^(da|true|1)$/i.test(trimmed);
  return trimmed;
}

/** Pagina sursă din evidența unui câmp („p2:l14” → 2). */
export function evidencePage(source: string | undefined): number | null {
  const m = /^p(\d+)/.exec(source ?? '');
  return m ? Number(m[1]) : null;
}

interface Props {
  document: DocumentWithDetails;
  /** câmpurile curente (corecturile peste propunere) */
  fields: Record<string, unknown>;
  docType: string;
  /** programul lotului; dacă lipsește, se deduce din cheile secțiunilor chestionarului */
  program?: RegistryQuestionnaireProgram | null;
  onFieldChange: (field: string, value: unknown) => void;
  onReclassify: (docType: string) => void;
  onShowPage: (page: number) => void;
}

/** Câmpurile extrase dintr-un document, editabile, cu încrederea și sursa fiecăruia. */
export default function DocumentFieldsPanel({ document, fields, docType, program, onFieldChange, onReclassify, onShowPage }: Props) {
  const proposal = document.proposal;
  const missing = new Set(proposal?.missingRequired ?? document.missingRequired ?? []);
  const fromGemini = new Set(proposal?.geminiFields ?? []);
  const confidence = proposal?.fieldConfidence ?? {};
  const evidence = proposal?.evidence ?? {};

  const shown = FIELDS_BY_DOC_TYPE[docType] ?? Object.keys(fields).filter((f) => !['labs', 'procedures', 'sections', 'extra'].includes(f));
  // câmpuri extrase care nu sunt în lista standard a tipului (nu se ascund)
  const extra = Object.keys(fields).filter(
    (f) => !shown.includes(f) && !['labs', 'procedures', 'sections', 'extra', 'lastName', 'firstName'].includes(f),
  );

  const renderField = (field: string) => {
    const value = fields[field];
    const isMissing = missing.has(field);
    const isGemini = fromGemini.has(field);
    const score = confidence[field];
    const noisy = typeof value === 'string' && !['cnp', 'hpBulletinNo'].includes(field) && looksLikeOcrNoise(value);
    const lowConfidence = (score !== undefined && score < 0.7) || noisy;
    const page = evidencePage(evidence[field]?.source);
    const border = isMissing ? '#ff4d4f' : isGemini || lowConfidence ? '#fa8c16' : undefined;

    return (
      <div key={field} style={{ marginBottom: 12 }} data-testid={`field-${field}`}>
        <Space size={6} wrap style={{ marginBottom: 4 }}>
          <Text strong>{FIELD_LABELS[field] ?? field}</Text>
          {isMissing && <Tag color="red">obligatoriu lipsă</Tag>}
          {isGemini && <Tag color="orange">din Gemini</Tag>}
          {noisy && <Tag color="orange">text citit cu greșeli — verifică în PDF</Tag>}
          {score !== undefined && score < 0.7 && !noisy && !isGemini && <Tag color="orange">încredere {Math.round(score * 100)}%</Tag>}
          {page && (
            <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onShowPage(page)}>
              p. {page}
            </Button>
          )}
        </Space>
        {field === 'consentStatus' ? (
          <Select
            value={(value as string) ?? undefined}
            options={CONSENT_OPTIONS}
            onChange={(v: string) => onFieldChange(field, v)}
            style={{ width: '100%' }}
          />
        ) : LONG_TEXT_FIELDS.has(field) ? (
          <Input.TextArea
            value={toEditable(field, value)}
            autoSize={{ minRows: 2, maxRows: 8 }}
            style={border ? { borderColor: border } : undefined}
            onChange={(e: { target: { value: string } }) => onFieldChange(field, fromEditable(field, e.target.value))}
          />
        ) : (
          <Input
            value={toEditable(field, value)}
            style={border ? { borderColor: border } : undefined}
            onChange={(e: { target: { value: string } }) => onFieldChange(field, fromEditable(field, e.target.value))}
          />
        )}
      </div>
    );
  };

  const labs = Array.isArray(fields.labs) ? (fields.labs as Array<Record<string, unknown>>) : [];
  const procedures = Array.isArray(fields.procedures) ? (fields.procedures as Array<Record<string, unknown>>) : [];
  const sections = fields.sections && typeof fields.sections === 'object' ? (fields.sections as Record<string, unknown>) : null;

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Text>Tip document:</Text>
        <Tooltip title="Reclasifică documentul dacă tipul detectat e greșit (se aplică la salvare)">
          <Select value={docType} options={RECLASSIFY_OPTIONS} onChange={(v: string) => onReclassify(v)} style={{ minWidth: 220 }} />
        </Tooltip>
        <Tag color={document.engine === 'GEMINI' ? 'orange' : 'blue'}>{ENGINE_LABELS[document.engine || 'NONE']}</Tag>
      </Space>

      {document.status === 'FAILED' && <Alert type="error" message={`Procesare eșuată: ${document.error ?? ''}`} style={{ marginBottom: 12 }} />}
      {docType === 'necunoscut' && (
        <Alert type="warning" message="Document neclasificat: alege tipul corect și salvează, sau lasă-l doar atașat." style={{ marginBottom: 12 }} />
      )}
      {DOC_TYPE_LABELS[docType] && ['imagistica', 'fotofinder'].includes(docType) && (
        <Alert type="info" message="Document doar atașat (fără extracție de câmpuri, D-04)." style={{ marginBottom: 12 }} />
      )}

      {shown.map(renderField)}
      {extra.length > 0 && (
        <Collapse
          size="small"
          items={[{ key: 'extra', label: `Alte câmpuri extrase (${extra.length})`, children: <div>{extra.map(renderField)}</div> }]}
          style={{ marginBottom: 12 }}
        />
      )}

      {procedures.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text strong>Proceduri</Text>
          <ul style={{ margin: '4px 0 0 18px' }}>
            {procedures.map((p, i) => (
              <li key={i}>{[p.code && `PO ${p.code}`, p.date, p.name].filter(Boolean).join(' · ')}</li>
            ))}
          </ul>
        </div>
      )}

      {labs.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Space size={6} wrap>
            <Text strong>Analize extrase ({labs.length})</Text>
            {(confidence.labs ?? 1) < 0.7 && (
              <Tag color="orange">citite automat din tabel — compară valorile cu PDF-ul</Tag>
            )}
          </Space>
          <Table
            size="small"
            rowKey={(l: Record<string, unknown>) => `${l.collectedAt}-${l.testName}`}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            dataSource={labs}
            columns={[
              { title: 'Analiză', dataIndex: 'testName', key: 'n' },
              { title: 'Valoare', key: 'v', render: (_: unknown, l: Record<string, unknown>) => `${l.value ?? ''} ${l.unit ?? ''}` },
              { title: 'Interval', dataIndex: 'refRange', key: 'r' },
              { title: 'Data', dataIndex: 'collectedAt', key: 'd' },
            ]}
          />
        </div>
      )}

      {sections && (
        <Collapse
          size="small"
          items={[
            {
              key: 'sections',
              label: `Răspunsurile din chestionar (${Object.keys(sections).length} secțiuni)`,
              children: (
                <RegistryQuestionnaireView
                  program={program ?? inferQuestionnaireProgram(sections)}
                  sections={sections}
                />
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
